"use client";

import { useRef, useState } from "react";

interface VideoPlayerProps {
  src: string;
  width?: number;
  height?: number;
  className?: string;
  showControls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  poster?: string; // preview image displayed before and while loading
  preload?: 'none' | 'metadata' | 'auto';
}

const VideoPlayer = ({
  src,
  width = 480,
  height = 360,
  className = "",
  showControls = true,
  autoPlay = false,
  muted = false,
  loop = false,
  poster,
  preload = 'metadata',
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleLoadStart = () => {
    setIsLoading(true);
    setError(null);
  };

  const handleCanPlay = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setError("Error loading video");
  };

  return (
    <div className={`relative ${className}`}>
      {isLoading && !error && (
        <div className="absolute inset-0 flex items-end justify-end p-2 pointer-events-none">
          <div className="text-[10px] md:text-xs bg-black/60 text-white rounded px-2 py-1 animate-pulse">
            Loading video…
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50 rounded">
          <div className="text-red-500">{error}</div>
        </div>
      )}

      <video
        ref={videoRef}
        src={src}
        width={width}
        height={height}
        controls={showControls}
        autoPlay={autoPlay}
        muted={muted}
        loop={loop}
        poster={poster}
        preload={preload}
        onLoadStart={handleLoadStart}
        onCanPlay={handleCanPlay}
        onError={handleError}
        className="border rounded bg-black object-contain w-full h-auto"
      />
    </div>
  );
};

export default VideoPlayer;
